/**
 * Selector de cliente (teams-child) para modales de facturación:
 * búsqueda en el desplegable + scroll infinito al navegar la lista.
 */

import { createHttpClient } from '@adapters/HttpClient';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import type { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export interface UseInvoiceModalTeamsComboboxOptions {
  /** Solo cargar equipos cuando el modal está abierto */
  enabled: boolean;
}

export const useInvoiceModalTeamsCombobox = ({ enabled }: UseInvoiceModalTeamsComboboxOptions) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new ClientsApiAdapter(httpClient), [httpClient]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['invoice-modal-teams-browse'],
    queryFn: ({ pageParam = 1 }) =>
      adapter.getClientsPaginated({
        page: pageParam as number,
        limit: 40,
        sortBy: 'name',
        sortDirection: 'asc',
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    enabled,
    staleTime: 2 * 60 * 1000,
  });

  const allClients = useMemo(() => data?.pages.flatMap((p) => p.clients) ?? [], [data]);

  const searchFn = useCallback(
    async (query: string): Promise<DynamicComboboxOption[]> => {
      const q = query.trim();
      if (q.length >= 2) {
        const page = await adapter.getClientsPaginated({
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortDirection: 'asc',
          name: q,
        });
        return page.clients.map((c) => ({
          label: c.name,
          value: String(c.id),
        }));
      }
      const source = !q
        ? allClients
        : allClients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
      return source.map((c) => ({
        label: c.name,
        value: String(c.id),
      }));
    },
    [adapter, allClients],
  );

  const onLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    searchFn,
    onLoadMore,
    hasMore: hasNextPage ?? false,
    isLoadingBrowse: isLoading,
  };
};
